import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { subjectsComplete } from 'src/app/common/app.helpers';
import { containsAnyOfSubstrings } from 'src/app/common/services/sentry-error-handler/sentry-error-helpers';
import { TwilioPromiseErrorMediator } from 'src/app/common/services/sentry-error-handler/twilio-promise-error-mediator';
import { environment } from 'src/environments/environment';
import { TwilioChatEngineError } from '../../helpers/twilio-error';
import { ChatEngineErrorService } from '../../interfaces/chat-engine/chat-engine-error.service';
import { ChatTraceData } from '../../interfaces/chat-model.interface';

export const TWILIO_ERROR_CONVERSATION_NOT_FOUND = 50350;

const TWILIO_ERROR_TEXT_DISCONNECTED = 'disconnected';
const TWILIO_ERROR_TEXT_TIMEOUT_PREFIX = 'Twilsock: request timeout';
const TWILIO_ERROR_TEXT_GET_TIMEOUT_SUFFIX = "request 'GET' to";

const TWILIO_ERROR_TEXT_CONNECTION_NOT_INITIALIZED = 'Unable to connect: Connection is not initialized';

const TWILIO_CONNECTION_STATE_ERROR_TEXTS: string[] = [
  TWILIO_ERROR_TEXT_DISCONNECTED,
  TWILIO_ERROR_TEXT_TIMEOUT_PREFIX,
  TWILIO_ERROR_TEXT_GET_TIMEOUT_SUFFIX,
];

export enum TwilioEventHandlers {
  ACTIVE_CHANNEL_MESSAGE_ADDED = 'TwilioActiveChannelService::messageAdded',
  ACTIVE_CHANNEL_MESSAGE_UPDATED = 'TwilioActiveChannelService::messageUpdated',
  ACTIVE_CHANNEL_TYPING_STARTED = 'TwilioActiveChannelService::typingStarted',
  ACTIVE_CHANNEL_TYPING_ENDED = 'TwilioActiveChannelService::typingEnded',
  ACTIVE_CHANNEL_PARTICIPANT_UPDATED = 'TwilioActiveChannelService::participantUpdated',

  CHANNELS_CONVERSATION_JOINED = 'TwilioChannelsService::conversationJoined',
  CHANNELS_CONVERSATION_UPDATED = 'TwilioChannelsService::conversationUpdated',
  CHANNELS_PARTICIPANT_JOINED = 'TwilioChannelsService::participantJoined',
  CHANNELS_PARTICIPANT_LEFT = 'TwilioChannelsService::participantLeft',
  CHANNELS_CONVERSATION_REMOVED = 'TwilioChannelsService::conversationRemoved',
  CHANNELS_MESSAGE_ADDED = 'TwilioChannelsService::messageAdded',
  CHANNELS_MESSAGE_REMOVED = 'TwilioChannelsService::messageRemoved',
  CHANNELS_MESSAGE_UPDATED = 'TwilioChannelsService::messageUpdated',

  MESSENGER_CONVERSATION_JOINED = 'TwilioMessengerService::conversationJoined',
  MESSENGER_CONVERSATION_UPDATED = 'TwilioMessengerService::conversationUpdated',
  MESSENGER_PARTICIPANT_JOINED = 'TwilioMessengerService::participantJoined',
  MESSENGER_PARTICIPANT_LEFT = 'TwilioMessengerService::participantLeft',
  MESSENGER_CONVERSATION_REMOVED = 'TwilioMessengerService::conversationRemoved',
  MESSENGER_MESSAGE_ADDED = 'TwilioMessengerService::messageAdded',
  MESSENGER_MESSAGE_REMOVED = 'TwilioMessengerService::messageRemoved',
  MESSENGER_MESSAGE_UPDATED = 'TwilioMessengerService::messageUpdated',
  MESSENGER_BLOCKED_LIST_UPDATED = 'TwilioMessengerService::blockedUsersListChanged',
}

export enum TwilioInternalOperations {
  CLIENT_INITIALIZATION = 'TwilioEngineLifecycleService::initEngine',
  CLIENT_SHUTDOWN = 'TwilioEngineLifecycleService::destroyEngine',
  CHANNELS_INITIALIZATION = 'TwilioChannelsService::loadInitialConversations',
  MESSENGER_INITIALIZATION = 'TwilioMessengerService::buildInitialMessengerContent',
  NOTIFICATIONS_INITIALIZATION = 'TwilioNotificationChannelService::openNotificationChannel',
  USER_INITIALIZATION = 'TwilioUsersService::initTwilioUser',
}

@Injectable()
export class TwilioErrorService extends ChatEngineErrorService implements OnDestroy {
  public get connectionStateRelatedErrorNoticed(): Observable<void> {
    return this.connectionStateRelatedErrorNoticed$.asObservable();
  }

  private connectionStateRelatedErrorNoticed$: Subject<void> = new Subject();

  private twilioPromiseErrorMediator = inject(TwilioPromiseErrorMediator);
  private sessionService = inject(SessionService);

  public constructor() {
    super();

    this.twilioPromiseErrorMediator.twilioUnhandledPromiseErrorOccured.subscribe((error: unknown) =>
      this.onUnhandledPromiseRejection(error),
    );
  }

  public ngOnDestroy(): void {
    subjectsComplete([this.connectionStateRelatedErrorNoticed$]);
  }

  public onOperationFailed(operationName: string, error: unknown, traceData: ChatTraceData): void {
    if (!this.handlingPrologue(error)) {
      return;
    }

    const errorMessage: string = `Operation: '${operationName}'. ` + this.generateTraceDetails(traceData);
    this.handlingEpilogue(errorMessage, error);
  }

  public onTwilioEventHandlerFailed(handlerName: string, error: unknown, traceData: ChatTraceData): void {
    if (!this.handlingPrologue(error)) {
      return;
    }

    const errorMessage: string = `Event: '${handlerName}'. ` + this.generateTraceDetails(traceData);
    this.handlingEpilogue(errorMessage, error);
  }

  public onTwilioInternalOperationFailed(
    internalOperationName: string,
    error: unknown,
    traceData: ChatTraceData,
  ): void {
    if (!this.handlingPrologue(error)) {
      return;
    }

    const errorMessage: string = `Internal phase: '${internalOperationName}'. ` + this.generateTraceDetails(traceData);
    this.handlingEpilogue(errorMessage, error);
  }

  public onUnhandledPromiseRejection(error: unknown): void {
    if (!this.handlingPrologue(error)) {
      return;
    }

    const errorMessage: string = `Unhandled promise. ` + this.currentUserTrace();
    this.handlingEpilogue(errorMessage, error);
  }

  private handlingPrologue(error: unknown): boolean {
    if (this.errorIndicatesConnectionState(error)) {
      this.connectionStateRelatedErrorNoticed$.next();
      return false;
    } else {
      return !this.errorIndicatesConnectionNotInitialized(error);
    }
  }

  private handlingEpilogue(errorMessage: string, error: unknown): void {
    if (!environment.production) {
      console.error(errorMessage);
      console.table(error);
    }

    this.twilioPromiseErrorMediator.onTwilioHandledError(new TwilioChatEngineError(errorMessage, error));
  }

  private errorIndicatesConnectionState(error: unknown): boolean {
    if (error instanceof Error && error.message) {
      return containsAnyOfSubstrings(error.message, TWILIO_CONNECTION_STATE_ERROR_TEXTS);
    }
    return false;
  }

  private errorIndicatesConnectionNotInitialized(error: unknown): boolean {
    if (error instanceof Error && error.message) {
      return error.message.includes(TWILIO_ERROR_TEXT_CONNECTION_NOT_INITIALIZED);
    }
    return false;
  }

  private generateTraceDetails(traceData: ChatTraceData): string {
    let trace: string = this.currentUserTrace();
    if (traceData.channelUniqueName) {
      trace += `Channel: ${traceData.channelUniqueName}. `;
    }
    if (traceData.messageId) {
      trace += `Message: ${traceData.messageId}. `;
    }
    if (traceData.participantId) {
      trace += `Participant: ${traceData.participantId}. `;
    }
    if (traceData.userId) {
      trace += `Other user: ${traceData.userId}. `;
    }
    return trace;
  }

  private currentUserTrace(): string {
    return this.sessionService.user?.profile.verified_email
      ? `User: ${this.sessionService.user.profile.verified_email}.`
      : 'No user active session';
  }
}
